import { recruiterEnv } from '@/config/env';
import { getKeycloak } from '@/auth/keycloak-instance';
import { triggerDownload } from '@/shared/xlsx';
import { EXPORT_TIMEOUT_MS } from './export-timeout';
import type { ProgramBaseType } from './program-export-availability';

/**
 * EU-739 — download da base por programa como ARQUIVO gerado pelo backend
 * (spec 080). Substitui o par "fetch JSON gigante + montar XLSX no browser"
 * da EU-654, que estourava memória em três processos com 90k inscritos.
 *
 * Molde do `downloadSubscribersBase` (EU-593): `fetch` cru — o download é
 * BINÁRIO, o `fetchClient` do SDK lê JSON/texto e corromperia os bytes —,
 * blob, nome vindo do `Content-Disposition`, download nativo. O fuso do
 * viewer viaja na query (`tz`): o backend roda em UTC e o "Concluído em"
 * precisa continuar no fuso do consultor.
 */

/** Erro do download com o status e o código nomeado do backend, p/ o toast. */
export class ProgramBaseDownloadError extends Error {
  readonly name = 'ProgramBaseDownloadError';
  constructor(
    readonly status: number,
    /** Código nomeado do Core (`program_has_no_stage`, `sheet_row_limit`…). */
    readonly code: string | null
  ) {
    super(`Program base export failed (status ${status}${code ? `, ${code}` : ''})`);
  }
}

const FALLBACK_FILE_NAME = 'base.xlsx';

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch {
    return match[1].trim();
  }
}

export interface ProgramBaseDownloadResult {
  fileName: string;
  /** Linhas de dados relatadas pelo backend; `null` = header ausente. */
  dataRows: number | null;
}

/**
 * Baixa a base do programa via BFF. Lança `ProgramBaseDownloadError` quando o
 * BFF não responde 2xx. O retorno informa `dataRows` (header
 * `x-base-export-rows`) para o toast distinguir a base VAZIA (FR-020a).
 */
export async function downloadProgramBase(
  programId: string,
  type: ProgramBaseType
): Promise<ProgramBaseDownloadResult> {
  const keycloak = getKeycloak();
  try {
    // Refresca o token antes do fetch cru (o SDK faz isso por request; aqui
    // não passamos pelo SDK). Falha → segue com o token atual → 401/403 claro.
    await keycloak.updateToken(30);
  } catch {
    // token expirado → o BFF responde 401/403; o toast de erro cobre.
  }
  const token = keycloak.token;

  const url = new URL(`${recruiterEnv.apiUrl}/programs/${programId}/base-export`);
  url.searchParams.set('type', type);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (tz) url.searchParams.set('tz', tz);

  const res = await fetch(url, {
    method: 'GET',
    headers: token ? { authorization: `Bearer ${token}` } : {},
    // Teto do CLIENTE — estritamente MENOR que todos os tetos da cadeia
    // (BFF→Core 280s < ALB 300s): o corte é sempre daqui, que é quem sabe
    // pintar o toast de erro.
    signal: AbortSignal.timeout(EXPORT_TIMEOUT_MS),
  });

  if (!res.ok) {
    const code = await res
      .json()
      .then((body: unknown) =>
        body && typeof body === 'object' && 'error' in body ? String(body.error) : null
      )
      .catch(() => null);
    throw new ProgramBaseDownloadError(res.status, code);
  }

  const blob = await res.blob();
  const fileName =
    filenameFromContentDisposition(res.headers.get('content-disposition')) ?? FALLBACK_FILE_NAME;
  const rowsHeader = res.headers.get('x-base-export-rows');
  triggerDownload(blob, fileName);
  return { fileName, dataRows: rowsHeader === null ? null : Number(rowsHeader) };
}
